import { del, get, post, put } from './http';

// Types sourced from https://github.com/ev-proddev/profile
// Paths below are relative to that repo root (profile-dto/src/main/java/com/experticity/profile/dto/).

// ── Types ─────────────────────────────────────────────────────────────────────

/** @source CategoryInterestDTO.java */
export type CategoryInterestDTO = {
  categoryId: number;
  name: string;
  iconCode: string;
  iconClass: string;
  modifiedTime: number;
  assetUuid: string;
  lightIconAssetPath: string;
  mediumIconAssetPath: string;
  heavyIconAssetPath: string;
  brandCount: string;
  hyphenatedName: string;
  favorite?: boolean;
  order?: number;
};

/** @source UserProductTaxonomyDTO.java */
export type UserProductTaxonomyDTO = {
  selectedProductTaxonomyIds: number[];
  filteredProductTaxonomyIds: number[];
};

/** @source ProfileUserDTO.java */
export type ProfileUserDTO = {
  userId: number;
  uuid: string;
  displayName: string;
  location: string;
  privateProfile: boolean;
  avatar: string;
  hero: string;
  profileLink: string;
};

/** @source PromptCardDTO.java */
export enum PromptLevel {
  Error = 'ERROR',
  Info = 'INFO',
  Success = 'SUCCESS',
  Warning = 'WARNING',
  White = 'WHITE',
}

/** @source PromptCardDTO.java */
export type PromptCardDTO = {
  cardImageUrl: string;
  dismissable: boolean;
  dismissKey: string;
  dismissText: string;
  dismissUrl: string;
  icon: string;
  level: PromptLevel;
  link: string;
  linkText: string;
  message: string;
  otherData: unknown;
  rank: number;
  subTitle: string;
  title: string;
  type: string;
};

/** @source PromptListDTO.java */
export type PromptListDTO = {
  completeness: number;
  dismissed: PromptCardDTO[];
  prompts: PromptCardDTO[];
  total: number;
};

/** @source UserConnectionDTO.java */
export type UserConnectionDTO = {
  providerKey: string;
  providerDisplayName: string;
  connected: boolean;
  expired: boolean;
  userAccountConnectionId: number;
  connectedTime: number | null;
  expirationTime: number | null;
  externalNetworkId: string;
  followersCount: number;
  followsCount: number;
  friendsCount: number;
  mediaCount: number;
  externalProfileLink: string;
  externalProfileName: string | null;
  externalProfileImageUrl: string;
  lastCrawlTime: number;
  bio: string;
  crawlInProgress: boolean;
  crawlErrors: string | null;
  username: string | null;
};

// ── API ───────────────────────────────────────────────────────────────────────

export function getActiveCategories(): Promise<CategoryInterestDTO[]> {
  return get<CategoryInterestDTO[]>('/xapi/profile/ext/1.0/categories/active');
}

export function putUserCategories(categories: CategoryInterestDTO[]): Promise<void> {
  return put('/xapi/profile/ext/1.0/users/categories', categories);
}

export function getUserProductTaxonomy(): Promise<UserProductTaxonomyDTO> {
  return get<UserProductTaxonomyDTO>('/xapi/profile/ext/1.0/user/productTaxonomy');
}

export function postUserProductTaxonomy(productTaxonomyId: number): Promise<void> {
  return post<void>(`/xapi/profile/ext/1.0/user/productTaxonomy/${productTaxonomyId}`);
}

export function deleteUserProductTaxonomy(productTaxonomyId: number): Promise<void> {
  return del(`/xapi/profile/ext/1.0/user/productTaxonomy/${productTaxonomyId}`);
}

export function getProfileUser(uuid: string): Promise<ProfileUserDTO> {
  return get<ProfileUserDTO>(`/xapi/profile/pub/1.0/users/${uuid}`);
}

export function getProfileStrength(): Promise<PromptListDTO> {
  return get<PromptListDTO>('/xapi/profile/ext/1.0/prompts/drips');
}

export function getSocialConnections(): Promise<UserConnectionDTO[]> {
  return get<UserConnectionDTO[]>('/xapi/profile/ext/1.0/connections');
}
